import React from 'react';
import { useAsset } from '../hooks/useAsset';
import { GameMode } from '../types';
import { Language, t } from '../data/translations';
import ScrollLeaderboard from './ScrollLeaderboard';
import ShellSlot from './shell/ShellSlot';
import { useShellLayout } from '../hooks/useShellLayout';
import { useMobileAutoScrollHint } from '../hooks/useMobileAutoScrollHint';

interface GameModeMenuProps {
  onStartGame: () => void;
  onNavigate: (mode: GameMode) => void;
  onBack: () => void;
  language: Language;
}

const GameModeMenu: React.FC<GameModeMenuProps> = ({ onStartGame, onNavigate, onBack, language }) => {
  const { isMobileShell } = useShellLayout();
  const menuBg = useAsset('ui.menu.bg');
  const btnPrimary = useAsset('ui.menu.btnPrimary');

  const menuButtonClass = isMobileShell
    ? 'min-h-[46px] text-base py-2.5 px-5'
    : 'min-h-[54px] text-lg py-3 px-6';



  const rootRef = React.useRef<HTMLDivElement>(null);
  useMobileAutoScrollHint({ targetRef: rootRef, sceneKey: 'game-mode-menu', variant: 'bottom-up' });

  return (
    <div
      ref={rootRef}
      className="relative w-full overflow-hidden text-white animate-[fadeIn_0.5s_ease-out]"
      style={{
        minHeight: 'var(--shell-viewport-height, 100vh)',
        backgroundImage: `url(${menuBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-slate-950/30" />
      <ShellSlot slot="menu-content">
        <div className={`relative z-10 flex min-h-full w-full flex-col items-center justify-center ${isMobileShell ? 'max-w-[380px]' : 'max-w-64'}`}>
          <h2 className={`${isMobileShell ? 'mb-4 text-2xl text-center' : 'mb-8 text-4xl'} font-bold text-white`}>
            {t('ui.selectGameMode', language)}
          </h2>

          <div className={`flex w-full flex-col ${isMobileShell ? 'gap-3' : 'gap-4'}`}>
            <button
              onClick={onStartGame}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
              {t('ui.withAI', language)}
            </button>
            <button
              onClick={() => onNavigate('MULTIPLAYER_MENU')}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
              {t('ui.multiplayer', language)}
            </button>
            <button
              onClick={onBack}
              className={`${isMobileShell ? 'mt-2' : 'mt-4'} ${menuButtonClass} text-slate-300 font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
              {t('ui.back', language)}
            </button>
          </div>

          {isMobileShell && (
            <ShellSlot slot="menu-secondary">
              <div className="flex w-full flex-col items-center gap-14 py-4">
                <ScrollLeaderboard
                  language={language}
                  variant="compact"
                />
                <ScrollLeaderboard
                  language={language}
                  title={t('ui.justDefeatedAI', language)}
                  endpoint="/api/ai-leaderboard"
                  variant="compact"
                />
              </div>
            </ShellSlot>
          )}
        </div>
      </ShellSlot>

      {!isMobileShell && (
        <>
          <ShellSlot slot="menu-secondary-start">
            <div className="pointer-events-auto">
              <ScrollLeaderboard language={language} title={t('ui.justDefeatedAI', language)} endpoint="/api/ai-leaderboard" />
            </div>
          </ShellSlot>

          <ShellSlot slot="menu-secondary-end">
            <div className="pointer-events-auto">
              <ScrollLeaderboard language={language} />
            </div>
          </ShellSlot>
        </>
      )}
    </div>
  );
};

export default GameModeMenu;
